import { useState } from "react";
import { Card, Group, NumberInput, Stack, Text } from "@mantine/core";
import useSWR from "swr";
import { calculateFeePreview, type FeeTier } from "../../utils/fee-preview";
import { formatMoney } from "../../utils/format";
import { TenantFeePolicyApi } from "../../apis";
import type { MyPlatformRate } from "../../apis/tenantFeePolicy";
import { SectionHeader } from "./SectionHeader";

const previewWash = { background: "light-dark(var(--mantine-color-brand-0), var(--mantine-color-dark-6))" };
const numeric = { fontVariantNumeric: "tabular-nums" as const };

interface Props {
  calculationMethod: "PERCENTAGE" | "FLAT" | "TIERED";
  feeRate?: string | null;
  flatFeeAmount?: string | null;
  tierConfig?: FeeTier[] | null;
  minFeeAmount?: string | null;
  maxFeeAmount?: string | null;
  feeCurrency: string;
  absorbPlatformFee?: boolean;
  chargeEndCustomerFee?: boolean;
}

export function FeePreviewSimulator(props: Props) {
  const [base, setBase] = useState<number>(50000);

  // B10 — the tenant's effective platform fee rate for this currency.
  const { data: platformRate } = useSWR<MyPlatformRate>(
    `swr.my.platform-rate.${props.feeCurrency}`,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    () => TenantFeePolicyApi.getPlatformRate(props.feeCurrency) as any,
    { revalidateOnFocus: false }
  );
  const rate = platformRate?.source === "CONTRACT" ? platformRate.feeRate : null;

  const r = calculateFeePreview({
    baseAmount: base,
    ...props,
    platformFeeRate     : rate,
    platformFeeMinAmount: platformRate?.source === "CONTRACT" ? platformRate.txFeeMinAmount : null,
    platformFeeMaxAmount: platformRate?.source === "CONTRACT" ? platformRate.txFeeMaxAmount : null
  });
  const fmt = (n: number) => formatMoney(n, props.feeCurrency);
  const ratePct = rate != null ? Number((Number(rate) * 100).toFixed(2)) : null;

  return (
    <Card withBorder padding="md" style={previewWash}>
      <Stack gap="sm">
        <SectionHeader eyebrow="Симуляци" title="Урьдчилан тооцоолол" />
        <Text size="sm" c="dimmed">Шимтгэл хэрхэн тооцогдохыг жишээ дүнгээр шалгана.</Text>
        <NumberInput label="Жишээ захиалгын дүн" value={base} min={0} hideControls thousandSeparator=","
          onChange={(v) => setBase(typeof v === "number" ? v : 0)} />
        {r.error ? (
          <Text c="dimmed" size="sm">Утга бүрэн бөглөсний дараа тооцоолол энд харагдана.</Text>
        ) : (
          <Stack gap="xs">
            <Group justify="space-between"><Text size="sm" c="dimmed">Захиалгын дүн (хэрэглэгч төлнө)</Text><Text size="sm" style={numeric}>{fmt(r.customerPays)}</Text></Group>
            <Group justify="space-between"><Text size="sm" c="dimmed">Тооцоолсон шимтгэл</Text><Text size="sm" style={numeric}>{fmt(r.calculatedFee)}</Text></Group>
            <Group justify="space-between">
              <Text size="sm" c="dimmed">Шимтгэл (танд ногдох){r.minApplied ? " (доод хязгаар)" : r.maxApplied ? " (дээд хязгаар)" : ""}</Text>
              <Text size="sm" style={numeric}>{fmt(r.finalFee)}</Text>
            </Group>
            {r.appliedTier && (
              <Text size="xs" c="dimmed">Түвшин: {r.appliedTier.fromAmount}–{r.appliedTier.toAmount ?? "∞"} @ {r.appliedTier.rate}</Text>
            )}

            <Group justify="space-between">
              <Text size="sm" c="dimmed">Харилцагчид үлдэх (цэвэр)</Text>
              <Text size="sm" fw={700} c="brand.7" style={numeric}>{fmt(r.netToMerchant)}</Text>
            </Group>

            {ratePct != null && r.platformFee != null && r.tenantNet != null ? (
              <>
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">Платформ авах ({ratePct}%)</Text>
                  <Text size="sm" style={numeric}>{fmt(r.platformFee)}</Text>
                </Group>
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">Таны цэвэр орлого (платформын дараа)</Text>
                  <Text size="sm" fw={700} c="brand.7" style={numeric}>{fmt(r.tenantNet)}</Text>
                </Group>
              </>
            ) : null}
          </Stack>
        )}
      </Stack>
    </Card>
  );
}
